import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, money0, fmtDateShort, fmtWhen, hours } from "../format.js";
import {
  Async, Badge, Bar, Button, Card, Grid, KPI, Money, SparkBars, Sep, Table, TopBar, Wrap, useAsync,
} from "../ui.jsx";

export default function Dashboard() {
  const { settings, me } = useApp();
  const sym = settings.currency_symbol;
  const state = useAsync(() => api.get("dashboard"), []);

  return (
    <>
      <TopBar crumb="Overview" title="Dashboard">
        {me.permissions["time.own"] && <Button onClick={() => navigate("/time")}>◷ Log time</Button>}
        {me.permissions["invoices.manage"] && (
          <Button kind="pri" onClick={() => navigate("/invoices/new")}>
            ＋ New invoice
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Async state={state}>
          {(d) => {
            const unbilledTotal = Number(d.unbilled.time.value) + Number(d.unbilled.expenses);
            return (
              <>
                <Grid cols={4}>
                  <KPI
                    label="Outstanding"
                    value={money(d.totals.outstanding, sym)}
                    sub={`${d.totals.open_count} open invoice${d.totals.open_count === 1 ? "" : "s"}`}
                  />
                  <KPI
                    label="Overdue"
                    value={money(d.totals.overdue_amount, sym)}
                    tone={d.totals.overdue_count ? "danger" : undefined}
                    sub={`${d.totals.overdue_count} invoice${d.totals.overdue_count === 1 ? "" : "s"} past due`}
                  />
                  <KPI
                    label="Unbilled work"
                    value={money(unbilledTotal, sym)}
                    sub={`${hours(d.unbilled.time.minutes)} hrs + expenses`}
                  />
                  <KPI
                    label="Collected this year"
                    value={money(d.revenue.ytd, sym)}
                    sub={`${money(d.revenue.this_month, sym)} this month`}
                  />
                </Grid>

                <Grid cols="1.55fr 1fr" className="mt" style={{ alignItems: "start" }}>
                  <Card title="Payments collected">
                    <SparkBars data={d.collections} format={(v) => money(v, sym)} />
                    <Sep />
                    <div className="stat-row">
                      <div>
                        <div className="k">Avg days to pay</div>
                        <div className="v">
                          {d.pace.avg_days_to_pay ? `${d.pace.avg_days_to_pay} days` : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="k">Collection rate</div>
                        <div className="v">{d.pace.collection_rate}%</div>
                      </div>
                      <div>
                        <div className="k">Avg invoice</div>
                        <div className="v">{money0(d.pace.avg_invoice, sym)}</div>
                      </div>
                    </div>
                  </Card>

                  <Card title="Needs your attention" bare>
                    {d.attention.map((i) => (
                      <div
                        key={i.id}
                        style={{ padding: "12px 16px", borderBottom: "1px solid var(--line-2)", cursor: "pointer" }}
                        onClick={() => navigate(`/invoices/${i.id}`)}
                      >
                        <div className="row">
                          <b>{i.number}</b>
                          <div className="grow" />
                          <Money v={i.balance} sym={sym} bold />
                        </div>
                        <div className="row tiny muted">
                          <span>{i.client_name}</span>
                          <div className="grow" />
                          <span style={{ color: "var(--danger)" }}>{i.days_late} days overdue</span>
                        </div>
                      </div>
                    ))}

                    {Number(d.unbilled.time.minutes) > 0 && (
                      <div
                        style={{ padding: "12px 16px", borderBottom: "1px solid var(--line-2)", cursor: "pointer" }}
                        onClick={() => navigate("/time")}
                      >
                        <div className="row">
                          <b>{hours(d.unbilled.time.minutes)} unbilled hours</b>
                          <div className="grow" />
                          <Money v={d.unbilled.time.value} sym={sym} bold />
                        </div>
                        <div className="tiny muted">
                          Across {d.unbilled.time.projects} project
                          {d.unbilled.time.projects === 1 ? "" : "s"} — ready to invoice
                        </div>
                      </div>
                    )}

                    {(d.payables?.due_soon || []).map((b) => (
                      <div
                        key={b.id}
                        style={{ padding: "12px 16px", borderBottom: "1px solid var(--line-2)", cursor: "pointer" }}
                        onClick={() => navigate("/bills")}
                      >
                        <div className="row">
                          <b>Pay {b.contractor_name}</b>
                          <div className="grow" />
                          <Money v={b.total} sym={sym} bold />
                        </div>
                        <div className="row tiny muted">
                          <span>{b.number ? `their invoice ${b.number}` : "contractor bill"}</span>
                          <div className="grow" />
                          {Number(b.days_late) > 0 ? (
                            <span style={{ color: "var(--danger)" }}>{b.days_late} days overdue</span>
                          ) : (
                            <span>due {fmtDateShort(b.due_on)}</span>
                          )}
                        </div>
                      </div>
                    ))}

                    {d.totals.draft_count > 0 && (
                      <div style={{ padding: "12px 16px", cursor: "pointer" }} onClick={() => navigate("/invoices")}>
                        <div className="row">
                          <b>
                            {d.totals.draft_count} draft invoice{d.totals.draft_count === 1 ? "" : "s"}
                          </b>
                          <div className="grow" />
                          <Money v={d.totals.draft_amount} sym={sym} bold />
                        </div>
                        <div className="tiny muted">Never sent</div>
                      </div>
                    )}

                    {!d.attention.length && !Number(d.unbilled.time.minutes) && !d.totals.draft_count &&
                      !(d.payables?.due_soon || []).length && (
                      <div className="empty sm" style={{ padding: 34 }}>
                        Nothing needs chasing. Everything is invoiced and current.
                      </div>
                    )}
                  </Card>
                </Grid>

                <Grid cols="1.55fr 1fr" className="mt" style={{ alignItems: "start" }}>
                  <Card
                    title="Recent invoices"
                    bare
                    right={
                      <Button sm kind="gho" onClick={() => navigate("/invoices")}>
                        View all →
                      </Button>
                    }
                  >
                    <Table
                      rows={d.recent}
                      onRowClick={(r) => navigate(`/invoices/${r.id}`)}
                      columns={[
                        { key: "number", label: "Invoice", render: (r) => <b>{r.number}</b> },
                        { key: "client_name", label: "Client" },
                        { key: "due_date", label: "Due", render: (r) => <span className="sm dim">{fmtDateShort(r.due_date)}</span> },
                        { key: "total", label: "Amount", align: "right", render: (r) => <Money v={r.total} sym={sym} /> },
                        {
                          key: "balance",
                          label: "Balance",
                          align: "right",
                          render: (r) => (Number(r.balance) ? <Money v={r.balance} sym={sym} /> : <span className="muted">—</span>),
                        },
                        { key: "display_status", label: "Status", render: (r) => <Badge status={r.display_status} /> },
                      ]}
                    />
                  </Card>

                  <Card title="Activity">
                    <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
                      {d.activity.map((a, i) => (
                        <div key={i}>
                          <div className="sm">{a.message}</div>
                          <div className="tiny muted">
                            {a.actor} · {fmtWhen(a.at)}
                          </div>
                        </div>
                      ))}
                      {!d.activity.length && <div className="sm muted">No activity yet.</div>}
                    </div>
                  </Card>
                </Grid>
              </>
            );
          }}
        </Async>
      </Wrap>
    </>
  );
}
